import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import ProspectingNav from '../../components/prospecting/ProspectingNav';
import { useTheme } from '../../contexts/ThemeContext';
import { TrendingUp, Users, MessageSquare, Target, ArrowDown, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Analytics() {
  const { isDark } = useTheme();
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [icpPerf, setIcpPerf] = useState([]);
  const [seqPerf, setSeqPerf] = useState([]);
  const [scoreDist, setScoreDist] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [days]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ov, fn, ip, sp, sd, tl] = await Promise.all([
        fetch(`${BACKEND_URL}/api/prospecting/analytics/overview?days=${days}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/prospecting/analytics/funnel`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/prospecting/analytics/icp-performance`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/prospecting/analytics/sequence-performance`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/prospecting/analytics/score-distribution`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/prospecting/analytics/activity-timeline?days=${days}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      ]);
      setOverview(ov); setFunnel(fn); setIcpPerf(ip || []); setSeqPerf(sp || []); setScoreDist(sd || []); setTimeline(tl || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;

  const StatCard = ({ label, value, sub, color = 'text-white' }) => (
    <div className={`${card} p-5`}>
      <p className={`text-3xl font-bold ${isDark ? color : 'text-gray-900'}`}>{value}</p>
      <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
      {sub && <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{sub}</p>}
    </div>
  );

  const maxTimelineVal = timeline.length > 0 ? Math.max(...timeline.map(t => Math.max(t.leads_created || 0, t.messages_sent || 0)), 1) : 1;

  if (loading) {
    return (
      <AppLayout title="Analytics">
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A2FF00]"></div></div>
      </AppLayout>
    );
  }

  const ov = overview || { total_leads: 0, new_leads_period: 0, funnel: {}, rates: {}, outreach: {}, sequences: {} };

  return (
    <AppLayout title="Analytics">
      <div className="space-y-6" data-testid="analytics-page">
        <ProspectingNav />
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Analytics</h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Performance metrics and conversion insights</p>
          </div>
          <select value={days} onChange={e => setDays(Number(e.target.value))} data-testid="period-select" className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300'} border`}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Leads" value={ov.total_leads} sub={`+${ov.new_leads_period} this period`} />
          <StatCard label="Conversion Rate" value={`${ov.rates?.conversion || 0}%`} color="text-emerald-400" />
          <StatCard label="Reply Rate" value={`${ov.outreach?.reply_rate || 0}%`} color="text-blue-400" />
          <StatCard label="Active Sequences" value={ov.sequences?.active || 0} sub={`${ov.sequences?.total_enrolled || 0} enrolled`} />
        </div>

        {/* Conversion Funnel */}
        <div className={`${card} p-5`}>
          <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Conversion Funnel</h2>
          {funnel?.funnel?.length > 0 ? (
            <div className="space-y-2">
              {funnel.funnel.map((step, i) => {
                const maxCount = Math.max(...funnel.funnel.map(s => s.count), 1);
                const width = Math.max((step.count / maxCount) * 100, 2);
                const colors = ['bg-blue-500', 'bg-gray-500', 'bg-yellow-500', 'bg-green-500', 'bg-purple-500', 'bg-teal-500', 'bg-indigo-500', 'bg-emerald-600'];
                return (
                  <div key={step.stage} className="flex items-center gap-3">
                    <span className={`w-24 text-xs capitalize ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{step.stage.replace('_', ' ')}</span>
                    <div className={`flex-1 h-8 rounded-lg overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                      <div className={`h-full rounded-lg ${colors[i % colors.length]} flex items-center px-2 transition-all duration-500`} style={{ width: `${width}%` }}>
                        <span className="text-xs font-medium text-white">{step.count}</span>
                      </div>
                    </div>
                    <span className={`w-12 text-right text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{step.percentage}%</span>
                  </div>
                );
              })}
            </div>
          ) : <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No funnel data yet</p>}
        </div>

        {/* Conversion Rates */}
        <div className={`${card} p-5`}>
          <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Stage Conversion Rates</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Qualification', value: ov.rates?.qualification, arrow: 'Leads → Qualified' },
              { label: 'Contact', value: ov.rates?.contact, arrow: 'Qualified → Contacted' },
              { label: 'Reply', value: ov.rates?.reply, arrow: 'Contacted → Replied' },
              { label: 'Conversion', value: ov.rates?.conversion, arrow: 'Total → Converted' },
            ].map(r => (
              <div key={r.label} className={`text-center p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                <p className={`text-2xl font-bold ${r.value > 20 ? 'text-green-400' : r.value > 5 ? 'text-yellow-400' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>{r.value || 0}%</p>
                <p className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{r.label}</p>
                <p className={`text-[10px] mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{r.arrow}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Score Distribution */}
          <div className={`${card} p-5`}>
            <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>ICP Score Distribution</h2>
            {scoreDist.length > 0 ? (
              <div className="flex items-end gap-1 h-32">
                {scoreDist.map((bucket, i) => {
                  const maxH = Math.max(...scoreDist.map(b => b.count), 1);
                  const height = Math.max((bucket.count / maxH) * 100, 2);
                  const color = i >= 6 ? 'bg-green-500' : i >= 3 ? 'bg-yellow-500' : 'bg-red-400';
                  return (
                    <div key={bucket.range} className="flex-1 flex flex-col items-center gap-1">
                      <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{bucket.count}</span>
                      <div className={`w-full rounded-t ${color}`} style={{ height: `${height}%` }} title={`${bucket.range}: ${bucket.count}`} />
                      <span className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{bucket.range}</span>
                    </div>
                  );
                })}
              </div>
            ) : <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No scored leads yet</p>}
          </div>

          {/* Activity Timeline */}
          <div className={`${card} p-5`}>
            <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Activity Timeline</h2>
            {timeline.length > 0 ? (
              <div className="space-y-1">
                <div className="flex items-center gap-4 mb-2">
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-500"></div><span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Leads</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-purple-500"></div><span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Messages</span></div>
                </div>
                <div className="flex items-end gap-px h-24">
                  {timeline.map((day, i) => (
                    <div key={day.date} className="flex-1 flex flex-col gap-px" title={`${day.date}: ${day.leads_created} leads, ${day.messages_sent} msgs`}>
                      <div className="bg-blue-500 rounded-t" style={{ height: `${Math.max((day.leads_created / maxTimelineVal) * 48, 1)}px` }} />
                      <div className="bg-purple-500 rounded-b" style={{ height: `${Math.max((day.messages_sent / maxTimelineVal) * 48, 1)}px` }} />
                    </div>
                  ))}
                </div>
                <div className="flex justify-between">
                  <span className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{timeline[0]?.date}</span>
                  <span className={`text-[9px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{timeline[timeline.length - 1]?.date}</span>
                </div>
              </div>
            ) : <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No activity data yet</p>}
          </div>
        </div>

        {/* ICP Performance */}
        {icpPerf.length > 0 && (
          <div className={`${card} p-5`}>
            <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>ICP Performance</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <th className={`text-left p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>ICP</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Scored</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Qualified</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Converted</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Avg Score</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Qual Rate</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Conv Rate</th>
                </tr></thead>
                <tbody>
                  {icpPerf.map(icp => (
                    <tr key={icp.icp_id} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <td className={`p-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{icp.name}</td>
                      <td className={`p-2 text-right ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{icp.leads_scored}</td>
                      <td className={`p-2 text-right ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{icp.qualified}</td>
                      <td className={`p-2 text-right ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{icp.converted}</td>
                      <td className={`p-2 text-right font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{icp.avg_score}</td>
                      <td className={`p-2 text-right ${icp.qualification_rate > 50 ? 'text-green-400' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>{icp.qualification_rate}%</td>
                      <td className={`p-2 text-right ${icp.conversion_rate > 10 ? 'text-emerald-400' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>{icp.conversion_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sequence Performance */}
        {seqPerf.length > 0 && (
          <div className={`${card} p-5`}>
            <h2 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Sequence Performance</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <th className={`text-left p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sequence</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Steps</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Enrolled</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Replied</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Converted</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Reply Rate</th>
                  <th className={`text-right p-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Conv Rate</th>
                </tr></thead>
                <tbody>
                  {seqPerf.map(seq => (
                    <tr key={seq.sequence_id} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                      <td className={`p-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {seq.name}
                        {!seq.is_active && <span className="ml-2 text-xs text-gray-500">(paused)</span>}
                      </td>
                      <td className={`p-2 text-right ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{seq.steps_count}</td>
                      <td className={`p-2 text-right ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{seq.enrolled}</td>
                      <td className={`p-2 text-right ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{seq.replied}</td>
                      <td className={`p-2 text-right ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{seq.converted}</td>
                      <td className={`p-2 text-right ${seq.reply_rate > 20 ? 'text-green-400' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>{seq.reply_rate}%</td>
                      <td className={`p-2 text-right ${seq.conversion_rate > 10 ? 'text-emerald-400' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>{seq.conversion_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
